import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { getLogger } from '@sas/nova-core/logging';
import { isNullish } from '@sas/nova-core/typeguards';
import cx from 'classnames';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useIsRTL } from '../../locale';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useFormatter } from '../../locale/formatting';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { postfixTestId } from '../../util/testId';
import { useDirectSpeech } from '../DirectSpeech/DirectSpeechProvider';
import { useKeyboardShortcuts } from '../KeyboardShortcuts';
import { Label } from '../Label';
import { Flow } from '../Layouts/Flow';
import { MessageDialog, useMessageDialogText } from '../MessageDialog';
import { Splitter } from '../Splitter';
import { getDualSelectorReducer, isItemSelectable } from './DataManagement';
import { DualSelectorLayout, isAlwaysSelectedItem } from './DualSelectorLayout/DualSelectorLayout';
import { PageableTableLayout } from './DualSelectorLayout/PageableTableLayout';
import { MoveButton } from './MoveButton/MoveButton';
import { ReorderButton } from './ReorderLayout/ReorderLayout';
import bundle from './NovaComponents-DualSelector-gui-icu.properties';
import './DualSelector.css';
import cn from './DualSelector.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const useHtmlId = createUseHtmlId('dual-selector');
const initialReorderButtonStates = [{
  disableUp: false,
  disableDown: false,
  disableDelete: false
}, {
  disableUp: false,
  disableDown: false,
  disableDelete: false
}];
const initialDoubleClickState = {
  clicked: false,
  selectedRow: -1
};
const initialCustomControlValueType = {
  value: '',
  errorStatus: 'none'
};
const logger = getLogger('DualSelector');
const emptyData = [];
export const DualSelector = props => {
  const localize = useTextLocalizer();
  let sourceLabelWithCount, disableSourceFilter;
  const disableTargetFilterArr = [];
  const targetLabelWithCountArr = [];
  let sourceTableIsSelected = true;
  const formatter = useFormatter();
  const dualSelectorId = useHtmlId(4);
  const directSpeech = useDirectSpeech();
  const dt = useDesignTokens();
  const isRTL = useIsRTL();
  const {
    getActionsByKeyEvent
  } = useKeyboardShortcuts();
  const {
    CONTINUE,
    CANCEL
  } = useMessageDialogText();
  const paginationProps = props.paginationProps;
  const hideAddButton = (props.hideAddButton || props.readOnly) ?? false;
  const hideAddAllButton = props.readOnly ? true : !!props.hideAddAllButton;
  const hideUndoButton = !!props.readOnly;
  const hideRemoveAllButton = !!props.hideAddAllButton;
  const disableAddAllButton = !!props.disableAddAllButton || !!props.disableAvailableList;
  const tokens = renderInlineCssVars({
    fg: dt.tokens['color-body-text'],
    bd: dt.tokens['color-container-border-4'],
    bg: dt.tokens['color-container-background-0'],
    'spacing-s': dt.tokens['space-block-s'],
    'spacing-m': dt.tokens['space-block-m'],
    'spacing-xs': dt.tokens['space-block-xs']
  }, 'dualselector');
  const actionButtonTokens = renderInlineCssVars({
    spacing: dt.tokens['space-base-null']
  }, 'dualselector-action-button');
  const initialMoveButtonState = useMemo(() => [{
    direction: 'add',
    disableOne: true,
    disableAll: disableAddAllButton ?? false,
    disableUndo: true
  }, {
    direction: 'add',
    disableOne: true,
    disableAll: disableAddAllButton ?? false,
    disableUndo: true
  }], [disableAddAllButton]);
  /* -------------------------------------------
   *  Copy the attributes so that the original do not change
   * ---------------------------------------------*/
  const source = {
    ...props.sourceAttribute
  };
  const target = {
    ...props.targetAttribute
  };
  const targetCopy = {
    ...target
  };

  /* -------------------------------------------
   *  Set default values if not passed in props
   * ---------------------------------------------*/

  const sourceLabel = source[0]?.label ?? localize('source.default.label');
  const targetLabel1 = target[0].label ?? localize('target.default.label');
  const targetLabel2 = target[1]?.label ?? localize('target.default.label');
  const key = props.dataFieldKeys[0];
  const hideReorder = props.paginationProps ? true : targetCopy[0].sortFunction || !!props.readOnly ? true : !!props.hideReorder;
  const hideDelete = !props.renderCustomControl || !!props.hideDeleteButton;

  /* -------------------------------------------
   *  Add alwaysSelected items to target.
   * ---------------------------------------------*/
  if (targetCopy[0].alwaysSelected) {
    targetCopy[0].alwaysSelected.forEach(item => {
      item._alwaysSelected = true;
    });
  }
  if (targetCopy[0].data === undefined && targetCopy[0].alwaysSelected !== undefined) {
    targetCopy[0].data = targetCopy[0].alwaysSelected;
  } else if (targetCopy[0].data !== undefined && targetCopy[0].alwaysSelected !== undefined) {
    targetCopy[0].data = [...new Set(targetCopy[0].data.concat(targetCopy[0].alwaysSelected))];
  }
  let targetData = [];
  targetData.push(targetCopy[0].data ? targetCopy[0].data.map(item => {
    return {
      ...item
    };
  }) : []);
  if (props.targetAttribute.length > 1) {
    targetData.push(targetCopy[1].data ? targetCopy[1].data.map(item => {
      return {
        ...item
      };
    }) : []);
  }
  let sourceData = source[0].data ? source[0].data.map(a => {
    return {
      ...a
    };
  }) : [];

  /* -------------------------------------------
   *  Define all state variables
   * ---------------------------------------------*/
  const [tableName, setTableName] = useState('source');
  const [selectedTableIndex, setSelectedTableIndex] = useState(0);
  const [buttonState, setButtonState] = useState(initialMoveButtonState);
  const [reorderButtonState, setReorderButtonState] = useState(initialReorderButtonStates);
  const [isOpen, setIsOpen] = useState(false);
  const removeAllRef = useRef(false);
  const doubleClickRef = useRef(initialDoubleClickState);
  const [customValue, setCustomValue] = useState(initialCustomControlValueType);
  const enableCustomAddBtnRef = useRef(false);
  const sourceLayoutRef = useRef();
  const targetLayoutRef = useRef();
  const reset = useCallback(() => {
    setTableName('source');
    setButtonState(initialMoveButtonState);
    setReorderButtonState(initialReorderButtonStates);
    setIsOpen(false);
    removeAllRef.current = false;
    doubleClickRef.current = initialDoubleClickState;
  }, [initialMoveButtonState]);
  const setCustomControlValue = customVal => {
    if (customVal) {
      setCustomValue(customVal);
      const regex = /^\s+$/;
      if (customVal.value !== '' && !regex.test(customVal.value.toString()) && customVal.errorStatus === 'none') {
        enableCustomAddBtnRef.current = true;
      } else {
        enableCustomAddBtnRef.current = false;
      }
    }
  };

  /* -------------------------------------------
   * Get selected rows from tablename = source/target
   * ---------------------------------------------*/
  const onSelectHandler = (selectedRowsValue, selectedTableName, selectedTableIndex) => {
    if (selectedRowsValue) {
      dsDispatch({
        type: 'selection',
        payload: {
          selectedRows: selectedRowsValue,
          tableName: selectedTableName,
          selectedTableIndex: selectedTableIndex ?? 0
        }
      });
      setTableName(selectedTableName);
      setSelectedTableIndex(selectedTableIndex ?? 0);
    }
  };

  /* -------------------------------------------
   * Directly move item from one table to another on doubleClick
   * ---------------------------------------------*/
  const displayStyle = props.mode === 'keep' && props.targetAttribute[0].displayStyle === 'single' ? 'single' : 'multiple';
  const onDoubleClickHandler = (selectedRow, selectedTableName, selectedTableIndex) => {
    if (selectedTableName === 'source' && props.disableAddButton) {
      return;
    }
    const index = selectedTableIndex ?? 0;
    const targetFilteredData = dsState.targetFilteredData?.[index];
    if (!!props.hideAvailableList || !!props.disableAvailableList && !(targetFilteredData ?? targetData[index])[selectedRow]?._custom) return;
    if (props.targetAttribute.length > 1 && selectedTableName === 'source') return;
    const direction = selectedTableName === 'source' ? 'add' : 'remove';
    if (paginationProps) {
      paginationProps.moveActionCallback(direction, selectedRow);
      return;
    }
    // AlwaysSelected items should not move
    if (!(selectedTableName === 'target' && dsState.alwaysSelected && isAlwaysSelectedItem(targetFilteredData ? targetFilteredData[selectedRow] : targetData[index][selectedRow]))) {
      if (direction === 'remove' && selectedTableName === 'target') {
        const item = targetData[index]?.[selectedRow];
        if (!!item?._custom) {
          doubleClickRef.current = {
            clicked: true,
            selectedRow
          };
          setIsOpen(true);
          setTableName(selectedTableName);
          return;
        }
      }
      dsDispatch({
        type: direction,
        payload: {
          selectedRows: [selectedRow],
          isMoveAll: false,
          mode: props.mode,
          targetDisplayStyle: displayStyle,
          layoutIndex: index
        }
      });
      setTableName(selectedTableName);
      setSelectedTableIndex(index);
    }
  };
  const onDoubleClick = (selectedRow, selectedTableName, selectedTableIndex) => {
    const actionHandler = shouldProceed => {
      if (shouldProceed) {
        onDoubleClickHandler(selectedRow, selectedTableName, selectedTableIndex);
      }
    };
    const direction = selectedTableName === 'source' ? 'add' : 'remove';
    if (props.onBeforeMoveAction && isAction(direction)) {
      props.onBeforeMoveAction(direction, actionHandler);
    } else {
      onDoubleClickHandler(selectedRow, selectedTableName, selectedTableIndex);
    }
  };
  const alwaysSelectedItems = useMemo(() => {
    const alwaysSelectedArr = [];
    const targetData = [...(props.targetAttribute[0].data ?? [])];
    const alwaysSelected = props.targetAttribute[0].alwaysSelected ?? [];
    targetData.forEach(item => {
      if (item.alwaysSelected) {
        alwaysSelectedArr.push(item);
      }
    });
    return [...new Set(alwaysSelectedArr.concat(alwaysSelected))];
  }, [props.targetAttribute]);

  /* -------------------------------------------
   *   Get Updated Data after Move buttons press
   * ---------------------------------------------*/

  const dualSelectorReducer = getDualSelectorReducer(props.mode, props.onTargetChange, props.selectedItemsThreshold);
  const [dsState, dsDispatch] = useReducer(dualSelectorReducer, {
    sourceData: sourceData ?? [],
    targetData: targetData ?? [],
    currentActionQueue: [],
    alwaysSelected: alwaysSelectedItems,
    focusAfterAction: true,
    customItems: []
  });
  const shouldApplySelectedItemsThreshold = !isNullish(props.selectedItemsThreshold) && props.selectedItemsThreshold > 0;
  const countLeftToMeetThreshold = props.selectedItemsThreshold - dsState.targetData[0].length;
  const compareFn = useCallback((a, b) => {
    const s1 = a[props.dataFieldKeys[0]];
    const s2 = b[props.dataFieldKeys[0]];
    if (s1 > s2) {
      return 1;
    } else if (s1 < s2) {
      return -1;
    } else {
      return 0;
    }
  }, [props.dataFieldKeys]);
  const sourceString = JSON.stringify([...(source[0].data ?? [])].sort(compareFn));
  const targetString = JSON.stringify([...(target[0].data ?? [])].sort(compareFn));
  const targetString1 = JSON.stringify([...(target[1]?.data ?? [])].sort(compareFn));
  useEffect(() => {
    reset();
    sourceLayoutRef.current?.reset();
    targetLayoutRef.current?.reset();
    const targetDataArr = [];
    targetDataArr.push(target[0].data ?? []);
    props.targetAttribute.length > 1 && targetDataArr.push(target[1].data ?? []);
    dsDispatch({
      type: 'reset',
      payload: {
        sourceData: source[0].data ?? [],
        targetData: [target[0].data ?? [], target[1]?.data ?? []],
        alwaysSelected: alwaysSelectedItems,
        currentActionQueue: [],
        itemSelections: undefined,
        sourceFilteredData: undefined,
        targetFilteredData: undefined,
        selectionTable: undefined,
        sourceSearchValue: undefined,
        targetSearchValue: undefined,
        key: undefined,
        narrator: undefined,
        focusAfterAction: true,
        customItems: [],
        isDataReset: true
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceString, targetString, targetString1, props.selectedItemsThreshold]);

  /* -------------------------------------------
   * Enable / Disable filter
   * ---------------------------------------------*/

  if (dsState.sourceData && dsState.sourceData.length === 0) {
    disableSourceFilter = true;
  }
  if (dsState.targetData) {
    if (dsState.targetData[0].length === 0) disableTargetFilterArr[0] = true;
    if (props.targetAttribute.length > 1 && dsState.targetData[1].length === 0) disableTargetFilterArr[1] = true;
  }

  /* -------------------------------------------
   * Get updated label
   * ---------------------------------------------*/

  const getUpdatedLabel = (filteredData, originalData, label, isSource = false) => {
    let updatedLabel;
    const originalCount = (props.mode === 'disable' || props.mode === 'keep') && isSource ? originalData.filter(item => isItemSelectable(item)).length : originalData.filter(item => !item.hasChildren).length;
    if (filteredData && filteredData.length < originalData.length) {
      const filteredCount = (props.mode === 'disable' || props.mode === 'keep') && isSource ? filteredData.filter(item => isItemSelectable(item)).length : filteredData.filter(item => !item.hasChildren).length;
      updatedLabel = localize('filtered.length.label', {
        label,
        filteredLength: formatter?.formatNumeric(filteredCount) ?? `${filteredCount}`,
        totalLength: formatter?.formatNumeric(originalCount) ?? `${originalCount}`
      });
    } else if (props.mode === 'disable' && isSource) {
      updatedLabel = localize('availableItems.length.label', {
        label,
        availableItemsLength: formatter?.formatNumeric(originalCount) ?? `${originalCount}`,
        totalLength: formatter?.formatNumeric(originalData.length) ?? `${originalData.length}`
      });
    } else {
      updatedLabel = localize('item.length.label', {
        label,
        length: formatter?.formatNumeric(originalCount) ?? `${originalCount}`
      });
    }
    return updatedLabel;
  };

  /* -------------------------------------------
   * Change label and filter values
   * ---------------------------------------------*/
  if (paginationProps) {
    const sourceRowCount = paginationProps.sourceTableProps[0].rowCount;
    const targetRowCount = paginationProps.targetTableProps?.[0]?.rowCount ?? targetData.length;
    sourceLabelWithCount = localize('item.length.label', {
      label: sourceLabel,
      length: formatter?.formatNumeric(sourceRowCount) ?? `${sourceRowCount}`
    });
    targetLabelWithCountArr[0] = localize('item.length.label', {
      label: targetLabel1,
      length: formatter?.formatNumeric(targetRowCount) ?? `${targetRowCount}`
    });
  } else {
    if (dsState.sourceFilteredData || dsState.targetFilteredData) {
      if (dsState.sourceFilteredData) {
        sourceData = dsState.sourceFilteredData;
        sourceLabelWithCount = getUpdatedLabel(dsState.sourceFilteredData, dsState.sourceData, sourceLabel, true);
        const targetDataArr = [];
        targetDataArr[0] = dsState.targetFilteredData?.[0] ?? dsState.targetData[0];
        targetDataArr[1] = dsState.targetFilteredData?.[1] ?? dsState.targetData[1];
        targetData = targetDataArr;
        for (let i = 0; i < props.targetAttribute.length; i++) {
          targetLabelWithCountArr[i] = getUpdatedLabel(dsState.targetFilteredData?.[i], dsState.targetData[i], i === 0 ? targetLabel1 : targetLabel2);
        }
      }
      if (dsState.targetFilteredData) {
        sourceData = dsState.sourceFilteredData ?? dsState.sourceData;
        sourceLabelWithCount = getUpdatedLabel(dsState.sourceFilteredData, dsState.sourceData, sourceLabel, true);
        for (let i = 0; i < props.targetAttribute.length; i++) {
          targetLabelWithCountArr[i] = getUpdatedLabel(dsState.targetFilteredData?.[i], dsState.targetData[i], i === 0 ? targetLabel1 : targetLabel2);
        }
        const targetDataArr = [];
        targetDataArr[0] = dsState.targetFilteredData[0] ?? dsState.targetData[0];
        targetDataArr[1] = dsState.targetFilteredData?.[1] ?? dsState.targetData[1];
        targetData = targetDataArr;
      }
    } else {
      sourceData = dsState.sourceData;
      targetData = dsState.targetData;
      sourceLabelWithCount = getUpdatedLabel(undefined, dsState.sourceData, sourceLabel, true);
      for (let i = 0; i < props.targetAttribute.length; i++) {
        targetLabelWithCountArr[i] = getUpdatedLabel(undefined, dsState.targetData[i], i === 0 ? targetLabel1 : targetLabel2);
      }
    }
  }

  /* -------------------------------------------
   *   Reorder button enable and disable
   * ---------------------------------------------*/
  useEffect(() => {
    if (paginationProps) return;
    const reorderButtonState = [{
      disableUp: true,
      disableDown: true,
      disableDelete: true
    }, {
      disableUp: true,
      disableDown: true,
      disableDelete: true
    }];
    const onlyCustomItemsSelected = (items, selectedRows, customItems) => {
      if (selectedRows.length === 0 || items.length === 0 || selectedRows[0] === '-1') {
        return false;
      }
      const selectedRowsNumbers = selectedRows.map(value => parseInt(value));
      if (customItems && customItems.length > 0 && selectedRowsNumbers?.length > 0) {
        for (const itemIndex of selectedRowsNumbers) {
          if (!customItems.includes(items[itemIndex]) && !items[itemIndex]._custom) {
            return false;
          }
        }
        return true;
      }
      return false;
    };
    const selectedRows = dsState.itemSelections;
    if (tableName === 'target' && !sourceTableIsSelected) {
      const selectedTargetIndex = dsState.selectedTableIndex ?? 0;
      const targetList = dsState.targetData[selectedTargetIndex];
      const targetFilteredList = dsState.targetFilteredData?.[selectedTargetIndex];
      if (displayStyle === 'multiple' || !haveDuplicatedItems(targetList)) {
        if (targetList.length > 0 && selectedRows && selectedRows.length > 0) {
          if (selectedRows && selectedRows.length > 0) {
            if (onlyCustomItemsSelected(targetFilteredList ?? targetList, selectedRows, dsState.customItems)) {
              reorderButtonState[selectedTargetIndex].disableDelete = false;
            }
            reorderButtonState[selectedTargetIndex].disableUp = false;
            reorderButtonState[selectedTargetIndex].disableDown = false;
            if (targetFilteredList) {
              // reordering is not allowed on filtered data
              reorderButtonState[selectedTargetIndex].disableUp = true;
              reorderButtonState[selectedTargetIndex].disableDown = true;
            } else {
              for (let index = 0; index < selectedRows.length; index++) {
                if (selectedRows[index] === '0') {
                  reorderButtonState[selectedTargetIndex].disableUp = true;
                }
                if (selectedRows[index] === (dsState.targetData[dsState.selectedTableIndex ?? 0].length - 1).toString()) {
                  reorderButtonState[selectedTargetIndex].disableDown = true;
                }
                // if the item is not reorderable, disable the up and down buttons
                const selectedRow = selectedRows[index];
                if (selectedRow !== undefined && targetList[parseInt(selectedRow)]?.isReorderable === false) {
                  reorderButtonState[selectedTargetIndex].disableUp = true;
                  reorderButtonState[selectedTargetIndex].disableDown = true;
                }
              }
            }
          }
        } else if (targetList.length === 0 || targetList.length > 0 && targetFilteredList && targetFilteredList.length > 0) {
          reorderButtonState[selectedTargetIndex].disableUp = true;
          reorderButtonState[selectedTargetIndex].disableDown = true;
        }
      } else {
        if (onlyCustomItemsSelected(targetFilteredList ?? targetList, selectedRows ?? [], dsState.customItems)) {
          reorderButtonState[selectedTargetIndex].disableDelete = false;
        }
        reorderButtonState[selectedTargetIndex].disableUp = true;
        reorderButtonState[selectedTargetIndex].disableDown = true;
      }
    }
    setReorderButtonState(reorderButtonState);
  }, [tableName, dsState.targetData, dsState.itemSelections, sourceTableIsSelected, dsState.targetFilteredData, dsState.customItems, props.dataFieldKeys, props.targetAttribute, displayStyle, paginationProps, dsState.selectedTableIndex]);
  useEffect(() => {
    if (!paginationProps) return;
    if (paginationProps.narrator) {
      const narrator = paginationProps.narrator;
      let message = '';
      if (paginationProps.narrator.action === 'undo') {
        message = localize(`afterAction.undo.${narrator.undoTo}.aria`);
      } else if (narrator.action === 'move') {
        message = localize(`afterAction.${narrator.direction}.aria`, {
          noOfSelectedRows: narrator.selectedRows ? narrator.selectedRows.toString() : '0'
        });
      }
      directSpeech?.notify(message);
    }
    const newButton = [{
      direction: 'add',
      disableOne: true,
      disableAll: false,
      disableUndo: true
    }, {
      direction: 'add',
      disableOne: true,
      disableAll: false,
      disableUndo: true
    }];
    const sourceLength = props.sourceAttribute[0]?.data?.length ?? 0;
    const targetLength = props.targetAttribute[0]?.data?.length ?? 0;
    const selectedItemsLength = paginationProps.selectedIndices?.length ?? 0;
    if (paginationProps.selectedTable === 'source') {
      if (sourceLength > 0 && selectedItemsLength > 0) {
        newButton[0].disableOne = false;
      }
    } else {
      if (targetLength > 0 && selectedItemsLength > 0) {
        newButton[0].disableOne = false;
        newButton[0].direction = 'remove';
      }
    }
    newButton[0].disableUndo = paginationProps.undoAction[0] ? false : true;
    setButtonState(newButton);
  }, [props.sourceAttribute, props.targetAttribute, paginationProps, directSpeech, localize]);

  /* -------------------------------------------
   *  Highlight selected item
   * ---------------------------------------------*/
  if (dsState.itemSelections && dsState.itemSelections.length > 0) {
    if (dsState.currentActionQueue && dsState.currentActionQueue.length > 0 && !dsState.selectionTable) {
      if (dsState.currentActionQueue[0].from === 'source') {
        sourceTableIsSelected = true;
      } else if (dsState.currentActionQueue[0].from === 'target') {
        sourceTableIsSelected = false;
      }
    } else {
      if (dsState.selectionTable === 'source') {
        sourceTableIsSelected = true;
      } else {
        sourceTableIsSelected = false;
      }
    }
  }

  /* -------------------------------------------
   *   Manage button enable and disable
   * ---------------------------------------------*/
  useEffect(() => {
    if (props.paginationProps) {
      return;
    }
    /* -------------------------------------------
     * Create message for directSpeech
     * ---------------------------------------------*/
    if (dsState.narrator) {
      let message = '';
      if (dsState.narrator.action === 'moveall') {
        message = localize(`directSpeech.afterAction.${dsState.narrator.direction}All.aria`, {
          targetListLabel: dsState.selectedTableIndex === 0 ? targetLabel1 : targetLabel2
        });
      } else if (dsState.narrator.action === 'undo') {
        // UndoTo is needed since dsState.currentActionQueue is emptied on undo
        message = localize(`afterAction.undo.${dsState.narrator.undoTo}.aria`);
      } else if (dsState.narrator.action === 'move') {
        message = localize(`directSpeech.afterAction.${dsState.narrator.direction}.aria`, {
          noOfSelectedRows: dsState.narrator.selectedRows ? dsState.narrator.selectedRows.toString() : '0',
          targetListLabel: dsState.selectedTableIndex === 0 ? targetLabel1 : targetLabel2
        });
      } else if (dsState.narrator.action === 'reorder') {
        message = localize(`afterAction.${dsState.narrator.direction}.aria`);
      } else if (dsState.narrator.action === 'customAdd') {
        message = localize('afterAction.customAdd.aria');
      } else if (dsState.narrator.action === 'filter') {
        const numberOfResults = dsState.selectionTable === 'source' ? !!dsState.sourceSearchValue ? dsState.sourceFilteredData?.length.toString() : '' : !!dsState.targetSearchValue && dsState.targetFilteredData ? dsState.targetFilteredData[dsState.selectedTableIndex ?? 0]?.length.toString() : '';
        if (!!numberOfResults) message = localize('filterResults.aria', {
          listLabel: numberOfResults
        });
      }
      directSpeech?.notify(message);
    }
    const newButton = [{
      direction: 'add',
      disableOne: true,
      disableAll: false,
      disableUndo: true
    }, {
      direction: 'add',
      disableOne: true,
      disableAll: false,
      disableUndo: true
    }];
    const sourceLength = (dsState.sourceFilteredData ?? dsState.sourceData).filter(item => isItemSelectable(item)).length;
    const index = dsState.selectedTableIndex ?? 0;
    const targetLength = (dsState.targetFilteredData?.[index] ?? dsState.targetData?.[index]).length;
    const selectedRows = dsState.itemSelections ?? [];
    const nonSelectedTargetListIndex = index === 0 ? 1 : 0;
    const nonSelectedTargetListLength = (dsState.targetFilteredData?.[nonSelectedTargetListIndex] ?? dsState.targetData?.[nonSelectedTargetListIndex])?.length;
    if (tableName === 'source') {
      if (sourceLength > 0) {
        if (selectedRows.length > 0) {
          newButton[0].disableOne = newButton[1].disableOne = false;
          if (shouldApplySelectedItemsThreshold) {
            if (sourceLength > countLeftToMeetThreshold) {
              newButton[0].disableAll = true;
              if (selectedRows.length > countLeftToMeetThreshold) {
                newButton[0].disableOne = true;
              }
            }
          }
        } else {
          // Condition: sourceLength > 0 && selectedRows === 0
          if (shouldApplySelectedItemsThreshold) {
            if (sourceLength > countLeftToMeetThreshold) {
              newButton[0].disableAll = true;
            }
          }
          newButton[0].disableOne = true;
          newButton[1].disableOne = true;
        }
      } else {
        // Condition: sourceLength === 0
        if (!Array.isArray(dsState.sourceFilteredData) && targetLength > 0) {
          newButton[0].direction = newButton[1].direction = 'remove';
          newButton[0].disableAll = newButton[1].disableAll = Array.isArray(dsState.alwaysSelected) && dsState.alwaysSelected.length > 0;
        } else {
          // Condition: sourceLength === 0 && sourceFilter applied
          newButton[0].disableAll = newButton[0].disableAll = true;
        }
      }
    } else if (tableName === 'target') {
      const targetList = dsState.targetData[dsState.selectedTableIndex ?? 0];
      const targetFilteredList = dsState.targetFilteredData?.[dsState.selectedTableIndex ?? 0];
      newButton[index].direction = 'remove';
      const nonCustomItemSelected = dsState.itemSelections?.some(row => !(targetFilteredList ?? targetList)[parseInt(row)]?._custom);
      const onlyCustomItemsPresent = !(targetFilteredList ?? targetList).some(item => !item?._custom);
      if (props.disableAvailableList) {
        newButton[index].disableOne = dsState.itemSelections && dsState.itemSelections.length > 0 ? nonCustomItemSelected ?? true : true;
        newButton[index].disableAll = targetLength > 0 && onlyCustomItemsPresent ? false : true;
      } else if (targetLength > 0) {
        if (selectedRows.length > 0) {
          newButton[index].disableOne = false;
          if (Array.isArray(dsState.alwaysSelected) && dsState.alwaysSelected.length > 0) {
            const selectedData = targetFilteredList ?? targetList;
            const alwaysSelectedItems = selectedData.filter(item => isAlwaysSelectedItem(item));
            newButton[index].disableAll = alwaysSelectedItems.length > 0 ? true : false;

            // iterating to see if any of the selected rows are alwaysSelected ones.
            for (const selectedValue of selectedRows.values()) {
              const targetItem = selectedData[Number(selectedValue)];
              if (targetItem && isAlwaysSelectedItem(targetItem)) {
                newButton[index].disableOne = true;
                break;
              }
            }
          }
        } else {
          // Condition: targetLength > 0 && selectedRows === 0
          if (Array.isArray(dsState.alwaysSelected) && dsState.alwaysSelected.length > 0) {
            const selectedData = targetFilteredList ?? targetList;
            const alwaysSelectedItems = selectedData.filter(item => isAlwaysSelectedItem(item));
            newButton[index].disableAll = alwaysSelectedItems.length > 0 ? true : false;
          }
        }
      } else {
        // Condition: targetLength === 0
        if (!Array.isArray(dsState.targetFilteredData?.[index]) && sourceLength > 0) {
          newButton[index].direction = 'add';
          if (shouldApplySelectedItemsThreshold) newButton[index].disableAll = sourceLength > countLeftToMeetThreshold;
        } else {
          // Condition: targetLength === 0 && targetFilter applied
          newButton[index].disableAll = true;
        }
      }
    }

    /* -------------------------------------------
     *   Handle Undo actions
     * ---------------------------------------------
     */

    const currentActionQueue = dsState.currentActionQueue;
    if (currentActionQueue && currentActionQueue.length > 0) {
      const index = currentActionQueue[0].selectedTableIndex ?? 0;
      newButton[index].disableUndo = false;
      if (currentActionQueue[0].from === 'source' && !Array.isArray(dsState.sourceFilteredData) && sourceLength === 0) {
        newButton[0].direction = 'remove';
        newButton[1].direction = 'remove';
        if (props.targetAttribute.length > 1 && nonSelectedTargetListLength === 0) {
          newButton[nonSelectedTargetListIndex].disableAll = true;
        }
      } else if (currentActionQueue[0].from === 'target' && !Array.isArray(dsState.targetFilteredData) && targetLength === 0) {
        newButton[index].direction = 'add';
      }
    } else {
      if (sourceLength > 0 && tableName === 'source') {
        newButton[0].direction = newButton[1].direction = 'add';
        if (props.targetAttribute.length > 1) {
          newButton[1].direction = newButton[1].direction = 'add';
        }
      } else if (targetLength > 0 && tableName === 'target') {
        newButton[0].direction = newButton[0].direction = 'remove';
        if (props.targetAttribute.length > 1) {
          newButton[1].direction = newButton[1].direction = 'remove';
        }
      }
    }
    // if disableAddButton or disableAddAllButton prop is set then disable add/add all buttons of both target lists (one to two DS)
    if (newButton[0].direction === 'add') {
      if (props.disableAddButton) {
        newButton[0].disableOne = newButton[1].disableOne = true;
      }
      if (disableAddAllButton) {
        newButton[0].disableAll = newButton[1].disableAll = true;
      }
    }
    setButtonState(newButton);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, dsState, tableName, sourceTableIsSelected, directSpeech, localize, props.mode, shouldApplySelectedItemsThreshold, countLeftToMeetThreshold, props.disableAddButton, disableAddAllButton]);
  const onMoveButtonClickHandler = (direction, isMoveAll = false, layoutIndex) => {
    const actionHandler = shouldProceed => {
      if (shouldProceed) {
        onMoveButtonClick(direction, isMoveAll, layoutIndex);
      }
    };
    if (props.onBeforeMoveAction) {
      props.onBeforeMoveAction(direction, actionHandler);
    } else {
      onMoveButtonClick(direction, isMoveAll, layoutIndex);
    }
  };

  /* -------------------------------------------
   *   Call and display MoveButton based on direction
   * ---------------------------------------------*/

  const makeAction = (direction, isMoveAll = false, disable = false, layoutIndex) => {
    let newDirection = direction;
    if (isMoveAll) {
      newDirection = `${direction}all`;
    } else if (direction === 'undo') {
      if (dsState.currentActionQueue[0] && dsState.currentActionQueue[0].from === 'source') {
        newDirection = `undoaddaction`;
      } else if (dsState.currentActionQueue[0] && dsState.currentActionQueue[0].from === 'target') {
        newDirection = 'undoremoveaction';
      }
    }
    let buttonAriaLabel = '';
    if (newDirection !== 'undo') {
      buttonAriaLabel = localize(`moveButtons.${newDirection}.button.aria`, {
        targetListLabel: layoutIndex === 0 ? targetLabel1 : targetLabel2
      });
    } else {
      if (props.targetAttribute.length === 1) {
        newDirection = 'undoOneToOne';
        buttonAriaLabel = localize(`moveButtons.${newDirection}.button.aria`);
      } else if (props.targetAttribute.length === 2) {
        newDirection = 'undoOneToTwo';
        buttonAriaLabel = localize(`moveButtons.${newDirection}.button.aria`, {
          sourceListLabel: sourceLabel,
          targetListLabel: layoutIndex === 0 ? targetLabel1 : targetLabel2
        });
      }
    }
    if (newDirection === 'custom_add' && props.customAddButtonAriaLabel) {
      buttonAriaLabel = props.customAddButtonAriaLabel;
    }
    return /*#__PURE__*/_jsx(MoveButton, {
      direction: direction,
      isMoveAll: isMoveAll,
      onPress: (direction, isMoveAll, layoutIndex) => {
        onMoveButtonClickHandler(direction, isMoveAll, layoutIndex);
      },
      disabled: disable,
      ariaLabel: buttonAriaLabel,
      customAddButtonTooltip: props.customAddButtonTooltip,
      testId: postfixTestId(props.testId, `moveButton${layoutIndex ?? 0 > 0 ? layoutIndex : ''}`),
      layoutIndex: layoutIndex ?? 0
    });
  };

  /* -------------------------------------------
   *  Callback on button click
   * ---------------------------------------------*/
  const onActionClick = (direction, isMoveAll = false, layoutIndex) => {
    let dsPayload;
    if (direction === 'custom_add') {
      const customKey = props.dataFieldKeys[0];
      const item = {
        [customKey]: customValue.value,
        _custom: true
      };
      dsPayload = {
        selectedRows: item,
        isMoveAll,
        mode: props.mode,
        targetDisplayStyle: 'multiple',
        key
      };
      enableCustomAddBtnRef.current = false;
    } else {
      dsPayload = {
        selectedRows: dsState.itemSelections ?? [],
        isMoveAll,
        mode: props.mode,
        targetDisplayStyle: displayStyle,
        layoutIndex: layoutIndex ?? 0,
        key
      };
    }
    if (direction === 'undo') {
      if (Array.isArray(dsState.currentActionQueue) && dsState.currentActionQueue.length > 0) {
        if (dsState.currentActionQueue[0].from === 'source' || dsState.currentActionQueue[0].from === 'target') {
          setTableName(dsState.currentActionQueue[0].from);
        }
      }
    }
    dsDispatch({
      type: direction,
      payload: dsPayload
    });
  };
  const onMoveButtonClick = (direction, isMoveAll = false, layoutIndex) => {
    if (props.paginationProps) {
      paginationProps && paginationProps.moveActionCallback(direction);
      return;
    }
    if (direction === 'remove') {
      let selectedItemsNum = [];
      let selectedItems;
      let targetItems = [];
      let targetData = [];
      const targetFilteredData = dsState.targetFilteredData?.[layoutIndex ?? 0];
      if (targetFilteredData) {
        targetData = targetFilteredData;
      } else {
        targetData = dsState.targetData[layoutIndex ?? 0];
      }
      if (dsState.itemSelections && dsState.itemSelections.length > 0) {
        // handle remove
        if (!isMoveAll) {
          selectedItems = dsState.itemSelections;
          selectedItemsNum = selectedItems.map(value => parseInt(value));
          for (const itemIndex of selectedItemsNum) {
            targetItems.push(targetData[itemIndex]);
          }
        } else {
          targetItems = targetData;
          if (haveCustomItems(dsState.itemSelections, targetItems, isMoveAll)) {
            removeAllRef.current = true;
          }
        }
      } else if (isMoveAll === true) {
        // handle remove all
        targetItems = targetData;
        removeAllRef.current = true;
      }

      // check if targetItems has any custom item
      if (!!targetItems.find(item => item._custom)) {
        setIsOpen(true);
        return;
      }
    }
    onActionClick(direction, isMoveAll, layoutIndex);
  };
  /* -------------------------------------------
   *   Add buttons based on direction
   * ---------------------------------------------*/
  const addButtons = () => {
    const addButtonsJsx = [];
    for (let i = 0; i < props.targetAttribute.length && i < 2; i++) {
      addButtonsJsx.push( /*#__PURE__*/_jsxs("div", {
        className: cn.actionButtonPanel,
        style: actionButtonTokens,
        children: [props.renderCustomControl &&
        // don't render for custom_add btn for 1to2
        makeAction('custom_add', false, shouldApplySelectedItemsThreshold && countLeftToMeetThreshold <= 0 || !enableCustomAddBtnRef.current), !hideUndoButton && makeAction('undo', false, buttonState[i].disableUndo, i), (!hideAddButton || buttonState[i].direction === 'remove') && makeAction(buttonState[i].direction, false, buttonState[i].disableOne, i), (!hideAddAllButton || !hideRemoveAllButton && buttonState[i].direction === 'remove') && makeAction(buttonState[i].direction, true, buttonState[i].disableAll, i)]
      }, i));
    }
    return addButtonsJsx;
  };
  const renderTargetList = (targetLabelWithCount, disableTargetFilterArr) => {
    const targetListJsx = [];
    for (let i = 0; i < props.targetAttribute.length && i < 2; i++) {
      targetListJsx.push( /*#__PURE__*/_jsxs(Flow, {
        spacing: "xs",
        className: cn.targetListContainer,
        children: [/*#__PURE__*/_jsxs("div", {
          className: cx(cn.contents, i === 1 && cn.targetListTopSpacing),
          children: [/*#__PURE__*/_jsx(Label, {
            className: cn.contentTitle,
            wrapText: false,
            id: dualSelectorId[3],
            testId: postfixTestId(props.testId, `targetLengthLabel${i > 0 ? i : ''}`),
            children: targetLabelWithCount[i]
          }), /*#__PURE__*/_jsx("div", {
            className: cx(cn.contentList),
            onKeyDown: event => onKeyDownHandler(event, 'target', i),
            "aria-labelledby": `${dualSelectorId[2]} ${dualSelectorId[3]}`,
            role: "group",
            children: !!paginationProps ? renderPageableTableLayout('target', paginationProps.targetTableProps?.[0]) : /*#__PURE__*/_jsx(DualSelectorLayout, {
              testId: postfixTestId(props.testId, `targetLayout${i > 0 ? i : ''}`),
              ref: targetLayoutRef,
              readOnly: !!props.readOnly,
              tableName: "target",
              type: 'list',
              mode: props.mode,
              dataFieldKeys: props.dataFieldKeys,
              data: targetData[i] ?? [],
              displayStyle: props.targetAttribute[i].displayStyle,
              hideSearch: props.targetAttribute[i].hideSearch ?? true,
              alwaysSelected: dsState.alwaysSelected,
              selectionCallback: (selectedRowsArray, tableName) => onSelectHandler(selectedRowsArray, tableName, i),
              selections: !sourceTableIsSelected ? selectedTableIndex === i ? dsState.itemSelections : [] : [],
              disableFilter: disableTargetFilterArr[i] ?? false,
              filterValue: (value, tableName) => onFilterValue(value, tableName, i),
              onDoubleClick: (selectedRow, tableName) => onDoubleClick(selectedRow, tableName, i),
              focusAfterAction: dsState.focusAfterAction,
              tableAriaLabel: targetLabel1,
              searchAriaLabel: localize('filterField.aria', {
                listLabel: i === 0 ? targetLabel1 : targetLabel2
              }),
              descriptionField: props.descriptionField,
              customItems: dsState.customItems,
              zeroStateText: props.targetAttribute[i].zeroStateText,
              isDataReset: dsState.isDataReset,
              sortFunction: target[0].sortFunction,
              numberOfResults: dsState.targetFilteredData && dsState.targetFilteredData?.length >= 0 ? dsState.targetFilteredData?.length : null
            })
          })]
        }), !hideReorder || !hideDelete ? /*#__PURE__*/_jsx("div", {
          className: cn.actionButtonPanel,
          style: actionButtonTokens,
          children: /*#__PURE__*/_jsx(ReorderButton, {
            onPress: onActionClick,
            reorderButtonState: reorderButtonState[i],
            targetLabel: i === 0 ? targetLabel1 : targetLabel2,
            testId: postfixTestId(props.testId, `reorderButton${i > 0 ? i : ''}`),
            hideReorder: hideReorder,
            hideDelete: hideDelete
          })
        }) : null]
      }, i));
    }
    return targetListJsx;
  };

  /* -------------------------------------------
   * Filter data as per searchValue
   * ---------------------------------------------*/
  const onFilterValue = (searchValue, tableName, selectedTableIndex) => {
    setTableName(tableName);
    dsDispatch({
      type: 'filter',
      payload: {
        table: tableName,
        searchValue,
        key,
        layoutIndex: selectedTableIndex ?? 0
      }
    });
  };
  const keyDownHandler = (event, tableName, selectedTableIndex) => {
    let isMoveAll = event.shiftKey && event.ctrlKey;
    const index = tableName === 'source' ? 0 : selectedTableIndex ?? 0;
    if (!!props.readOnly) {
      return;
    }
    if (isMoveAll && buttonState[index].disableAll) {
      return;
    }
    if (!!props.hideAvailableList) {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;
    }
    const {
      actions
    } = getActionsByKeyEvent(event.nativeEvent);
    if (actions.includes('undo') && !buttonState[index].disableUndo) {
      dsDispatch({
        type: 'undo',
        payload: {
          selectedRows: dsState.itemSelections ?? [],
          isMoveAll: false,
          mode: props.mode,
          layoutIndex: index,
          key
        }
      });
      return;
    }
    const sourceLength = (dsState.sourceFilteredData ?? dsState.sourceData).filter(item => isItemSelectable(item)).length;
    const targetLength = (dsState.targetFilteredData?.[index] ?? dsState.targetData[index]).length;
    const targetFilteredList = dsState.targetFilteredData?.[index];
    const targetList = dsState.targetData[index];
    const targetHasCustomItems = haveCustomItems(dsState.itemSelections, targetFilteredList ?? targetList, isMoveAll);
    const targetHasUnmovableItems = haveUnmovableItems(dsState.itemSelections, targetFilteredList ?? targetList, isMoveAll);
    let direction;
    switch (event.key) {
      case 'ArrowRight':
        if (tableName === 'source' && !isRTL && sourceLength !== 0 || tableName === 'target' && isRTL && targetLength !== 0 && !targetHasUnmovableItems) {
          if (props.targetAttribute.length > 1 && tableName === 'source' && !isRTL) return;
          if (paginationProps) {
            paginationProps.moveActionCallback(isRTL ? 'remove' : 'add', undefined, tableName);
            return;
          } else {
            if (!isMoveAll && (dsState.itemSelections && dsState.selectionTable !== tableName || buttonState[index].disableOne)) {
              return;
            } else if (isMoveAll) {
              removeAllRef.current = true;
            }
            if (targetHasCustomItems && isRTL) {
              setIsOpen(true); // Open the confirmation dialog
              return;
            }
            direction = isRTL ? 'remove' : 'add';
          }
        }
        break;
      case 'ArrowLeft':
        if (tableName === 'target' && !isRTL && targetLength !== 0 && !targetHasUnmovableItems || tableName === 'source' && isRTL && sourceLength !== 0) {
          if (props.targetAttribute.length > 1 && tableName === 'source' && isRTL) return;
          if (paginationProps) {
            paginationProps.moveActionCallback(isRTL ? 'add' : 'remove', undefined, tableName);
            return;
          } else {
            if (!isMoveAll && (dsState.itemSelections && dsState.selectionTable !== tableName || buttonState[index].disableOne)) {
              return;
            } else if (isMoveAll) {
              removeAllRef.current = true;
            }
            if (targetHasCustomItems && !isRTL) {
              setIsOpen(true); // Open the confirmation dialog
              return;
            }
            direction = isRTL ? 'add' : 'remove';
          }
        }
        break;
      case 'ArrowUp':
        if (tableName === 'target' && !sourceTableIsSelected && event.altKey && event.shiftKey && !reorderButtonState[index].disableUp) {
          direction = 'up';
          isMoveAll = false;
        }
        break;
      case 'ArrowDown':
        if (tableName === 'target' && !sourceTableIsSelected && event.altKey && event.shiftKey && !reorderButtonState[index].disableDown) {
          direction = 'down';
          isMoveAll = false;
        }
        break;
      default:
        return;
    }
    direction && dsDispatch({
      type: direction,
      payload: {
        selectedRows: dsState.itemSelections ?? [],
        isMoveAll,
        mode: props.mode,
        targetDisplayStyle: displayStyle,
        layoutIndex: index
      }
    });
  };
  const onKeyDownHandler = (event, tableName, selectedTableIndex) => {
    let direction;
    const isMoveAll = event.shiftKey && event.ctrlKey;
    const index = tableName === 'source' ? 0 : selectedTableIndex ?? 0;
    if (event.key === 'ArrowRight') {
      direction = isRTL ? 'remove' : 'add';
      if (!isMoveAll && (dsState.itemSelections && dsState.selectionTable !== tableName || buttonState[index].disableOne)) {
        return;
      }
    } else if (event.key === 'ArrowLeft') {
      direction = isRTL ? 'add' : 'remove';
      if (!isMoveAll && (dsState.itemSelections && dsState.selectionTable !== tableName || buttonState[index].disableOne)) {
        return;
      }
    }
    const actionHandler = shouldProceed => {
      if (shouldProceed) {
        keyDownHandler(event, tableName, selectedTableIndex);
      }
    };
    if (props.onBeforeMoveAction && isAction(direction)) {
      props.onBeforeMoveAction(direction, actionHandler);
    } else {
      keyDownHandler(event, tableName, selectedTableIndex);
    }
  };
  const getWarningDialogTitle = () => {
    if (removeAllRef.current) {
      return localize('moveButton.removeAllButton.warningDialog.title');
    } else {
      return localize('moveButton.removeButton.warningDialog.title');
    }
  };
  const getWarningDialogMessage = () => {
    if (removeAllRef.current) {
      return localize('moveButton.removeAllButton.warningDialog.msg');
    } else {
      return localize('moveButton.removeButton.warningDialog.msg');
    }
  };
  useEffect(() => {
    if (shouldApplySelectedItemsThreshold && dsState.targetData[0].length > props.selectedItemsThreshold) logger.error(`There are ${dsState.targetData[0].length} items selected by default which exceeds the set threshold limit of ${props.selectedItemsThreshold}.`);
  }, [shouldApplySelectedItemsThreshold, props.selectedItemsThreshold, dsState.targetData]);
  const renderPageableTableLayout = (tableName, pageableLayoutProps) => {
    const ariaLabel = tableName === 'source' ? sourceLabel : targetLabel1;
    return /*#__PURE__*/_jsx(PageableTableLayout, {
      testId: postfixTestId(props.testId, `${tableName}Pageablelayout`),
      tableName: tableName,
      type: tableName === 'source' ? props.type : props.targetType ?? 'list',
      mode: 'move',
      data: tableName === 'source' ? props.sourceAttribute[0].data ?? [] : props.targetAttribute[0].data ?? [],
      parentNodeSelectable: paginationProps?.parentNodeSelectable,
      tableState: pageableLayoutProps.tableState,
      dataFieldKeys: props.dataFieldKeys,
      zeroStateText: pageableLayoutProps.zeroStateText,
      renderCell: pageableLayoutProps.renderCell,
      rowCount: pageableLayoutProps.rowCount ?? 0,
      loadData: pageableLayoutProps.loadData,
      threshold: pageableLayoutProps.threshold,
      hideSearch: pageableLayoutProps.hideSearch ?? true,
      searchValue: pageableLayoutProps.searchValue,
      columns: pageableLayoutProps.columns,
      selectionCallback: paginationProps.selectionCallback,
      selections: getSelections(tableName),
      disableFilter: pageableLayoutProps.disableFilter ?? false,
      filterValue: paginationProps?.onFilterValue ?? (() => {}),
      onDoubleClick: onDoubleClickHandler,
      focusAfterAction: pageableLayoutProps.focusAfterAction ?? true,
      tableAriaLabel: ariaLabel,
      searchAriaLabel: localize('searchField.aria', {
        listLabel: ariaLabel
      }),
      accessibilityInfo: pageableLayoutProps.accessibilityInfo,
      numberOfResults: pageableLayoutProps.numberOfResults ?? null,
      ...(tableName === 'source' && {
        preventInitialAutoFocus: props.preventInitialAutoFocus
      }),
      ...(props.type === 'tree' && 'renderCellData' in pageableLayoutProps ? {
        renderCellData: pageableLayoutProps.renderCellData,
        onExpand: pageableLayoutProps.onExpand,
        getRowKey: pageableLayoutProps.getRowKey,
        getRowIndex: pageableLayoutProps.getRowIndex,
        getChildNodesRowKey: pageableLayoutProps.getChildNodesRowKey,
        getParentsRowKey: pageableLayoutProps.getParentsRowKey,
        getDataNode: pageableLayoutProps.getDataNode,
        getDataNodeContainer: pageableLayoutProps.getDataNodeContainer
      } : {})
    });
  };
  const getSelections = tableName => {
    if (paginationProps) {
      if (paginationProps.selectedTable === tableName) {
        return paginationProps.selectedIndices;
      }
      return emptyData;
    }
  };
  const getSections = () => {
    if (props.hideAvailableList) return [{
      minSize: 0,
      defaultSize: '0%',
      resizable: false
    }, {
      minSize: 200
    }];
    return [{
      minSize: 200,
      defaultSize: '50%'
    }, {
      minSize: 200
    }];
  };
  return /*#__PURE__*/_jsxs("div", {
    className: cn.container,
    style: {
      ...tokens,
      ...props.style
    },
    role: "region",
    "data-testid": props.testId,
    "aria-label": props.ariaLabel,
    "aria-labelledby": props.ariaLabelledBy,
    "aria-describedby": props.ariaDescribedBy,
    children: [/*#__PURE__*/_jsxs(Splitter, {
      style: {
        height: '100%',
        width: '100%'
      },
      sections: getSections(),
      hideSplitterBar: true,
      testId: postfixTestId(props.testId, 'Splitter') // eslint-disable-line @sas/i18n/jsx-to-localize
      ,
      children: [!props.hideAvailableList && /*#__PURE__*/_jsxs("div", {
        className: cn.sourcePanel,
        children: [/*#__PURE__*/_jsxs("div", {
          className: cn.contents,
          children: [/*#__PURE__*/_jsx(Label, {
            className: cn.contentTitle,
            wrapText: false,
            id: dualSelectorId[1],
            testId: postfixTestId(props.testId, 'sourceLengthLabel'),
            children: sourceLabelWithCount
          }), /*#__PURE__*/_jsx("div", {
            className: cn.contentList,
            onKeyDown: event => onKeyDownHandler(event, 'source'),
            "aria-labelledby": `${dualSelectorId[0]} ${dualSelectorId[1]}`,
            role: "group",
            children: !!paginationProps ? renderPageableTableLayout('source', paginationProps.sourceTableProps[0]) : /*#__PURE__*/_jsx(DualSelectorLayout, {
              testId: postfixTestId(props.testId, 'sourceLayout'),
              ref: sourceLayoutRef,
              readOnly: !!props.readOnly,
              tableName: "source",
              disableList: props.disableAvailableList,
              type: props.type,
              mode: props.mode,
              dataFieldKeys: props.dataFieldKeys,
              data: sourceData ?? [],
              hideSearch: props.sourceAttribute[0].hideSearch ?? true,
              selectionCallback: onSelectHandler,
              selections: sourceTableIsSelected ? dsState.itemSelections : [],
              disableFilter: (disableSourceFilter || props.disableAvailableList) ?? false,
              filterValue: onFilterValue,
              onDoubleClick: onDoubleClick,
              focusAfterAction: dsState.focusAfterAction,
              tableAriaLabel: sourceLabel,
              searchAriaLabel: localize('filterField.aria', {
                listLabel: sourceLabel
              }),
              descriptionField: props.descriptionField,
              zeroStateText: props.sourceAttribute[0].zeroStateText,
              isDataReset: dsState.isDataReset,
              sortFunction: source[0].sortFunction,
              disableSort: source[0].disableSort,
              preventInitialAutoFocus: props.preventInitialAutoFocus,
              numberOfResults: dsState.sourceFilteredData && dsState.sourceFilteredData?.length >= 0 ? dsState.sourceFilteredData?.length : null
            })
          }), props.renderCustomControl && /*#__PURE__*/_jsx("div", {
            className: cn.customField,
            children: props.renderCustomControl(setCustomControlValue, shouldApplySelectedItemsThreshold ? countLeftToMeetThreshold > 0 : !props.readOnly)
          })]
        }), /*#__PURE__*/_jsx(Flow, {
          orientation: "vertical",
          className: cn.addButtonsContainer,
          children: addButtons()
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: cx(cn.targetPanel, cn.targetPanelMultiple),
        children: renderTargetList(targetLabelWithCountArr, disableTargetFilterArr)
      })]
    }), /*#__PURE__*/_jsx(MessageDialog, {
      type: "warning",
      isOpen: isOpen,
      testId: postfixTestId(props.testId, 'warningDialog'),
      title: getWarningDialogTitle(),
      text: getWarningDialogMessage(),
      firstAction: {
        text: CONTINUE,
        onPress: () => {
          if (doubleClickRef.current.clicked) {
            dsDispatch({
              type: 'remove',
              payload: {
                selectedRows: [doubleClickRef.current.selectedRow],
                isMoveAll: false,
                mode: props.mode,
                targetDisplayStyle: displayStyle
              }
            });
            doubleClickRef.current = {
              clicked: false,
              selectedRow: -1
            };
          } else if (removeAllRef.current) {
            onActionClick('remove', true);
            removeAllRef.current = false;
          } else {
            onActionClick('remove');
          }
          setIsOpen(false);
        }
      },
      dismissAction: {
        text: CANCEL,
        onPress: () => {
          if (doubleClickRef.current.selectedRow !== -1) {
            onSelectHandler([doubleClickRef.current.selectedRow.toString()], 'target');
          }
          doubleClickRef.current = {
            clicked: false,
            selectedRow: -1
          };
          removeAllRef.current = false;
          setIsOpen(false);
        },
        isDefault: true
      }
    })]
  });
};
function haveDuplicatedItems(data) {
  const clone = [...data];
  const index = clone.findIndex(item => {
    const firstIndex = data.indexOf(item);
    const lastIndex = data.lastIndexOf(item);
    return firstIndex !== data.length - lastIndex - 1;
  });
  return index > -1;
}
function haveCustomItems(selectionRows, items, moveAll) {
  if (!!moveAll) {
    return !!items.find(item => item._custom);
  }
  return !!selectionRows?.find(row => items?.[parseInt(row)]?._custom);
}
function haveUnmovableItems(selectionRows, items, moveAll) {
  if (!!moveAll) {
    return !!items.find(item => item._alwaysSelected);
  }
  return !!selectionRows?.find(row => items?.[parseInt(row)]?._alwaysSelected);
}
function isAction(direction) {
  return direction === 'add' || direction === 'remove' || direction === 'undo';
}
//# sourceMappingURL=DualSelector.js.map